import {
	DEFAULT_CONFS,
	DEFAULT_DIVS,
	LEAGUE_DATABASE_VERSION,
	PHASE,
} from "../../common/constants.ts";
import type { Conditions, GetLeagueOptions } from "../../common/types.ts";
import { shuffle } from "../../common/random.ts";
import { MP_FUTURE_DRAFT_PICK_SEASONS } from "../../common/multiplayerConfig.ts";
import { unwrapGameAttribute } from "../../common/unwrapGameAttribute.ts";
import { draft, phase, realRosters } from "../core/index.ts";
import { idb } from "../db/index.ts";
import { helpers } from "../util/index.ts";
import { getDefaultSettings } from "../views/newLeague.ts";
import { getClient, getLobbyState, setLocalLid } from "./index.ts";
import { exportSnapshotBlob } from "./exportSnapshot.ts";
import {
	applyMultiTeamMode,
	getMyClaimedTid,
	importSnapshotToLeague,
	settingsFromGameAttributes,
} from "./importSnapshot.ts";
import { invalidateMpLidCache, markSeqApplied } from "./sync.ts";
import { seedAssets } from "./trades.ts";

// This creates an import cycle (api/index.ts -> api/multiplayer.ts -> here),
// which is safe because the api object is only dereferenced at call time, long
// after all modules have finished evaluating.
import api from "../api/index.ts";

const snapshotPath = (leagueId: string) => `${leagueId}/league.json.gz`;

// Matches what the normal New League UI passes for a real-players league
// (NewLeague/index.tsx sets basicInfo.version = LEAGUE_DATABASE_VERSION).
// version MUST be set: with version undefined, augmentPartialPlayer takes a
// legacy-league-file path that rewrites EVERY draft prospect's draft.year to
// the current season - collapsing all future real draft classes (Durant,
// Mobley, etc.) into the start year's draft.
const emptyFromFile = {
	gameAttributes: undefined,
	hasRookieContracts: true,
	maxGid: undefined,
	startingSeason: undefined,
	teams: undefined,
	version: LEAGUE_DATABASE_VERSION,
};

// Commissioner only. Creates the local league, scrambles all players with an
// all-AI fantasy draft (so nobody inherits a dynasty), uploads the resulting
// snapshot, and activates the league so everyone else can import it.
export const startLeague = async (
	leagueId: string,
	conditions: Conditions,
): Promise<
	| { status: "unclaimed_members"; count: number }
	| { status: "done"; lid: number }
> => {
	const supabase = getClient();

	const started = (await supabase.rpc("start_league", {
		p_league_id: leagueId,
	})) as { data: { status: string; count?: number } | null; error: any };
	if (started.error) {
		throw new Error(started.error.message);
	}
	if (started.data?.status === "unclaimed_members") {
		return { status: "unclaimed_members", count: started.data.count ?? 0 };
	}

	const { league: mpLeague, members, myTid } = await getMyClaimedTid(leagueId);
	if (started.data?.status === "already_started") {
		// Allow the commissioner to retry a start that crashed partway through,
		// but never re-run it on a league that is already live.
		if (mpLeague.status !== "starting") {
			throw new Error("This league has already been started");
		}
	}

	// Real players (with the lobby's chosen starting season) or random players
	const mpSettings = mpLeague.settings;
	let leagueSettings = getDefaultSettings();
	let getLeagueOptions: GetLeagueOptions | undefined;
	let confs = DEFAULT_CONFS;
	let divs = DEFAULT_DIVS;
	let teamsFromInput: any = helpers.getTeamsDefault();
	let keptKeys: string[] = [];
	if (mpSettings.rosters === "real") {
		getLeagueOptions = {
			type: "real",
			season: mpSettings.startingSeason!,
			phase: PHASE.PRESEASON,
			randomDebuts: false,
			randomDebutsKeepCurrent: false,
			realDraftRatings: leagueSettings.realDraftRatings ?? "rookie",
			realStats: "none",
			includePlayers: true,
		};
		const info = await realRosters.getLeagueInfo(getLeagueOptions);
		// Seed the settings from the era's game attributes, exactly like the
		// New League screen does (getSettingsFromGameAttributes). Without this,
		// getDefaultSettings()'s MODERN values (e.g. $150M cap, play-in, nba2027
		// lottery) get applied as overrides and clobber the era-correct rules -
		// so a 2011 league would run with a $150M cap and play-in games, which
		// also wildly miscalibrates contract demands. Real rule changes still
		// arrive over time via the scheduledEvents store (processed in
		// phase.finalize as the league advances through the years).
		leagueSettings = settingsFromGameAttributes(info.gameAttributes as any);
		confs =
			(unwrapGameAttribute(info.gameAttributes as any, "confs") as any) ??
			DEFAULT_CONFS;
		divs =
			(unwrapGameAttribute(info.gameAttributes as any, "divs") as any) ??
			DEFAULT_DIVS;
		teamsFromInput = info.teams;
		keptKeys = info.stores;
	}
	// League difficulty chosen on the create-lobby form (default Normal = 0)
	if (typeof mpSettings.difficulty === "number") {
		leagueSettings.difficulty = mpSettings.difficulty;
	}
	// Longer tradable-draft-pick horizon than vanilla (genPicks below reads this
	// to pre-generate that many future seasons of picks). Existing leagues get
	// raised lazily by the runner instead (see beginSegment in simRunner.ts).
	leagueSettings.numSeasonsFutureDraftPicks = MP_FUTURE_DRAFT_PICK_SEASONS;

	const lid = await api.main.createLeague(
		{
			name: mpLeague.name,
			tid: myTid,
			file: undefined,
			url: undefined,
			shuffleRosters: false,
			importLid: undefined,
			getLeagueOptions,
			keptKeys,
			confs,
			divs,
			teamsFromInput,
			settings: leagueSettings,
			fromFile: emptyFromFile,
			startingSeasonFromInput: undefined,
			leagueCreationID: `mp-${leagueId}`,
		},
		conditions,
	);

	// v12: the league opens INTO a live fantasy draft. Pool every player and
	// set up a full snake draft in random team order - but make NO picks here.
	// The snapshot below is exported at PHASE.FANTASY_DRAFT with the whole
	// order pending; everyone imports it, then the runner starts the live
	// draft (humans pick on their own screens, AI and auto-drafters are picked
	// by the runner).
	const leagueTeams = await idb.cache.teams.getAll();
	const draftOrder = leagueTeams.filter((t) => !t.disabled).map((t) => t.tid);
	shuffle(draftOrder);
	await phase.newPhase(PHASE.FANTASY_DRAFT, conditions, draftOrder);

	// Normalize draft picks exactly like the snapshot import will on everyone
	// else's machine (createStream runs genPicks with realPlayers=false for
	// file imports, which dedupes real-data oddities like a team holding two
	// of its own second-rounders; fantasy-draft picks are preserved while the
	// league is in the fantasy draft phase). Without this, the creator's pick
	// set differs from every importer's.
	await draft.genPicks({});

	await applyMultiTeamMode(members, myTid);

	// Seed the trade-validation ownership ledger from the post-redraft state
	await seedAssets(leagueId);

	const blob = await exportSnapshotBlob(mpLeague.name);
	const upload = await supabase.storage
		.from("snapshots")
		.upload(snapshotPath(leagueId), blob, {
			upsert: true,
			contentType: "application/gzip",
		});
	if (upload.error) {
		throw new Error(`Snapshot upload failed: ${upload.error.message}`);
	}

	const activated = await supabase.rpc("activate_league", {
		p_league_id: leagueId,
		p_snapshot_path: snapshotPath(leagueId),
	});
	if (activated.error) {
		throw new Error(activated.error.message);
	}

	await setLocalLid(leagueId, lid);
	invalidateMpLidCache();

	return { status: "done", lid };
};

// Non-commissioners: download the latest canonical snapshot and import it as
// a local league, then mark all snapshot-baked events as applied.
export const joinStartedLeague = async (
	leagueId: string,
	conditions: Conditions,
): Promise<{ lid: number }> => {
	const { league: mpLeague } = await getLobbyState(leagueId);
	if (mpLeague.status !== "active" || !mpLeague.snapshot_path) {
		throw new Error("This league has not been started yet");
	}

	const { lid } = await importSnapshotToLeague({
		leagueId,
		snapshotPath: mpLeague.snapshot_path,
		conditions,
	});

	await markSeqApplied(leagueId, mpLeague.snapshot_seq ?? 0);
	invalidateMpLidCache();

	return { lid };
};
